var _excluded = ["appearance", "actionButton", "body", "children", "closeButton", "header", "icon", "isOpen", "onClose"];

var _jsxFileName = "/Users/krise/gitlab/drywall/src/components/Toast/Toast.jsx",
    _this = this;

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import CloseButton from '../CloseButton/CloseButton';
/**
 * Toast is a subtle messages that appears at the top or bottom of the screen.
 * They're used as an informational feedback mechanism for user initiated actions.
 * They are specific the "app" or experience that a user is currently in.
 * Toasts are ephemeral, organic notifications that relate directly to an action the user has taken.
 * While they should draw attention, they should not interrupt users from performing primary tasks.
 *
 * See also [`ToastProvider`](#toastprovider).
 *
 * ##### Accessibility
 * * https://www.w3.org/TR/wai-aria-practices/#alert
 *
 * ##### Inspiration
 * * https://getbootstrap.com/docs/4.3/components/toasts/
 * * https://material-ui.com/components/snackbars/
 * * https://lightningdesignsystem.com/components/toast/
 * * https://ant.design/components/alert/
 * * https://blueprintjs.com/docs/#core/components/toast
 */

var Toast = styled(function (props) {
  var appearance = props.appearance,
      actionButton = props.actionButton,
      body = props.body,
      children = props.children,
      closeButton = props.closeButton,
      header = props.header,
      icon = props.icon,
      isOpen = props.isOpen,
      onClose = props.onClose,
      rest = _objectWithoutProperties(props, _excluded);

  if (!isOpen) {
    return null;
  }

  var closeCallback = React.useCallback(function () {
    // Call existing click function if it exists
    if (closeButton && closeButton.props && typeof closeButton.props.onClick === 'function') {
      closeButton.props.onClick();
    }

    if (typeof onClose === 'function') {
      onClose();
    }
  }, [closeButton, onClose]);
  var childrenContent = children;

  if (typeof children === 'function') {
    var iconContent = icon;

    if (typeof icon === 'function') {
      iconContent = icon(props);
    }

    var closeContent = closeButton;

    if (closeButton && onClose) {
      closeContent = /*#__PURE__*/React.cloneElement(closeButton, {
        onClick: closeCallback
      });
    }

    childrenContent = children(Object.assign({}, props, {
      icon: iconContent,
      closeButton: closeContent
    }));
  }

  return /*#__PURE__*/React.createElement("div", _extends({}, rest, {
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 72,
      columnNumber: 12
    }
  }), childrenContent);
}).withConfig({
  displayName: "Toast",
  componentId: "sc-nim36i-0"
})([""]);
Toast.Icon = styled.div.withConfig({
  displayName: "Toast__Icon",
  componentId: "sc-nim36i-1"
})([""]);
Toast.Action = styled.div.withConfig({
  displayName: "Toast__Action",
  componentId: "sc-nim36i-2"
})([""]);
Toast.Close = styled.div.withConfig({
  displayName: "Toast__Close",
  componentId: "sc-nim36i-3"
})([""]);
Toast.Body = styled.div.withConfig({
  displayName: "Toast__Body",
  componentId: "sc-nim36i-4"
})([""]);
Toast.Header = styled.header.withConfig({
  displayName: "Toast__Header",
  componentId: "sc-nim36i-5"
})([""]);
Toast.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * An optional action button.
   */
  actionButton: PropTypes.node,

  /**
   * Toasts can come in different styles that carry different semantic meanings.
   * e.g. "info", "success", "warning", "error"
   */
  appearance: PropTypes.string,

  /**
   * The body content of the toast message.
   */
  body: PropTypes.node,

  /**
   * In general, you will want to use the `body` and `header` props,
   * but you can use the `children` prop to override the default component layout.
   * When passing a function, the function will receive all the component props as the only
   * argument (Note: some of the props may have been modified and/or enhanced,
   * so you should always use the values that are passed to the render function).
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

  /**
   * The close button node. You can set this to null to remove the close button from the toast.
   */
  closeButton: PropTypes.node,

  /**
   * The header content of the toast message.
   *
   * It is uncommon to have a header in a toast component, but some systems may want to include
   * it in their toast layout.
   */
  header: PropTypes.node,

  /**
   * An icon node or a function that returns an icon node.
   * The function will be passed the component's props as the one and only parameter.
   */
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

  /**
   * Toasts are a controlled component where the `isOpen` prop controls the visibility.
   */
  isOpen: PropTypes.bool,

  /**
   * Function called with the `closeButton` is clicked.
   */
  onClose: PropTypes.func,

  /**
   * Toasts should have a role of [status](https://www.w3.org/TR/wai-aria-1.1/#status) or
   * [alert](https://www.w3.org/TR/wai-aria-1.1/#alert) to signal assistive technologies
   * that it requires the user's attention. In general, you will always want to use the less
   * strict "status" role.
   */
  role: PropTypes.oneOf(['status', 'alert'])
} : {};
Toast.defaultProps = {
  // eslint-disable-next-line zillow/react/prop-types
  children: function children(_ref) {
    var icon = _ref.icon,
        header = _ref.header,
        body = _ref.body,
        actionButton = _ref.actionButton,
        closeButton = _ref.closeButton;
    return /*#__PURE__*/React.createElement(React.Fragment, {
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 139,
        columnNumber: 9
      }
    }, icon && /*#__PURE__*/React.createElement(Toast.Icon, {
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 140,
        columnNumber: 22
      }
    }, icon), header && /*#__PURE__*/React.createElement(Toast.Header, {
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 141,
        columnNumber: 24
      }
    }, header), body && /*#__PURE__*/React.createElement(Toast.Body, {
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 142,
        columnNumber: 22
      }
    }, body), actionButton && /*#__PURE__*/React.createElement(Toast.Action, {
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 143,
        columnNumber: 30
      }
    }, actionButton), closeButton && /*#__PURE__*/React.createElement(Toast.Close, {
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 144,
        columnNumber: 29
      }
    }, closeButton));
  },
  closeButton: /*#__PURE__*/React.createElement(CloseButton, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 147,
      columnNumber: 18
    }
  }),
  isOpen: true,
  role: 'status'
};
/** @component */

export default Toast;